import React from 'react'
import { useNavigate } from 'react-router-dom'

export interface BreadcrumbItem {
    label: string
    to?: string
}

export type BadgeVariant = 'primary' | 'purple' | 'blue' | 'green' | 'orange'

export interface PageHeaderProps {
    title: string
    subtitle?: string
    breadcrumbs?: BreadcrumbItem[]
    actions?: React.ReactNode
    badge?: string
    badgeVariant?: BadgeVariant
}

const badgeClasses: Record<BadgeVariant, string> = {
    primary: 'bg-red-100 text-red-700 dark:bg-primary/20 dark:text-red-400',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
    orange: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
}

export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    breadcrumbs,
    actions,
    badge,
    badgeVariant = 'primary'
}) => {
    const navigate = useNavigate()

    return (
        <header className="mb-8">
            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="flex items-center gap-2 mb-3 text-sm">
                    {breadcrumbs.map((crumb, idx) => (
                        <React.Fragment key={idx}>
                            {idx > 0 && (
                                <span className="material-symbols-outlined text-[14px] text-gray-500">
                                    chevron_right
                                </span>
                            )}
                            {crumb.to ? (
                                <button
                                    onClick={() => navigate(crumb.to!)}
                                    className="text-text-muted hover:text-primary transition-colors"
                                >
                                    {crumb.label}
                                </button>
                            ) : (
                                <span className="text-gray-400">{crumb.label}</span>
                            )}
                        </React.Fragment>
                    ))}
                </nav>
            )}

            {/* Title Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white font-display">{title}</h1>
                            {badge && (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${badgeClasses[badgeVariant]}`}>
                                    {badge}
                                </span>
                            )}
                        </div>
                        {subtitle && (
                            <p className="text-sm text-text-muted mt-1">{subtitle}</p>
                        )}
                    </div>
                </div>
                {actions && (
                    <div className="flex flex-wrap items-center gap-3">
                        {actions}
                    </div>
                )}
            </div>
        </header>
    )
}

export default PageHeader

